import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess, AppRoleService, checkIdentityRole } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  AppRoleDims,
  AppRoleKey,
  MonitoringOriginEntityComposite,
  MonitoringOriginEntityEntity,
  SimpleCollection,
  WithUuid,
} from '@nk/data-entities-lib';
import { Observable, switchMap } from 'rxjs';
import {
  FetchMonitoringOriginEntityCompositeCollectionParams,
  getMonitoringOriginEntityCompositeHttpParams,
} from '../utils/screening-overview.utils';

@Injectable({
  providedIn: 'root',
})
export class MonitoringOriginEntityService {
  constructor(
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
    private _appRoleService: AppRoleService,
  ) {}

  public checkIdentityRole(): Observable<AppRoleDims> {
    return this._appRoleService.activeAppRoleDimsCache.get().pipe(checkIdentityRole([AppRoleKey.aml]));
  }

  public monitoringOriginEntityCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchMonitoringOriginEntityCompositeCollection,
  );
  public fetchMonitoringOriginEntityCompositeCollection(
    params: FetchMonitoringOriginEntityCompositeCollectionParams,
  ): Observable<SimpleCollection<MonitoringOriginEntityComposite>> {
    let httpParams = new HttpParams();
    httpParams = getMonitoringOriginEntityCompositeHttpParams(params);

    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.get<SimpleCollection<MonitoringOriginEntityComposite>>(
          `/screening-config/${params.screeningConfigUuid}/monitoring-origin-entity-composite-collection`,
          { params: httpParams },
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public monitoringOriginEntityCompositeCache = this._cachingService.create(
    this,
    this.fetchMonitoringOriginEntityComposite,
  );
  public fetchMonitoringOriginEntityComposite(
    screeningConfigUuid: string,
    monitoringOriginEntityUuid: string,
  ): Observable<MonitoringOriginEntityComposite> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.get<MonitoringOriginEntityComposite>(
          `/screening-config/${screeningConfigUuid}/monitoring-origin-entity-composite/${monitoringOriginEntityUuid}`,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public postMonitoringOriginEntityEntity(
    screeningConfigUuid: string,
    payload: Pick<MonitoringOriginEntityEntity, 'screening_config_uuid' | 'screening_entity_uuid' | 'monitored_flag'>,
  ): Observable<WithUuid<MonitoringOriginEntityEntity>> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<WithUuid<MonitoringOriginEntityEntity>>(
          `/screening-config/${screeningConfigUuid}/monitoring-origin-entity`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }
}
